import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteMeta } from '../data/mock';

/**
 * SEO Component
 * @param {string} title - Page title
 * @param {string} description - Page description
 * @param {string} keywords - Page keywords
 * @param {string} image - OG image URL
 * @param {string} url - Canonical URL
 * @param {string} type - OG type (website, article, etc.)
 */
const serviceMap = {
  "/services/branding": {
    name: "Brand Engineering & Design",
    desc: "Transform your identity with Meraki Solutions. We specialize in visual identity, brand strategy, and creative direction to build digital legacies.",
    url: "https://meraki-solutions.in/services/branding"
  },
  "/services/film-making": {
    name: "Cinematic Film Production",
    desc: "Craft visual masterpieces that engage, inspire, and convert. Meraki Solutions combines high-end production value with narrative depth.",
    url: "https://meraki-solutions.in/services/film-making"
  },
  "/services/video-editing": {
    name: "Video Editing & Post-Production",
    desc: "High-end post-production, color grading, sound design, and narrative editing to make your videos stand out.",
    url: "https://meraki-solutions.in/services/video-editing"
  },
  "/services/motion-graphics": {
    name: "Motion Graphics & CGI",
    desc: "Premium 3D animation, VFX, kinetic typography, and product renders that elevate digital stories.",
    url: "https://meraki-solutions.in/services/motion-graphics"
  },
  "/services/web-development": {
    name: "Web Development & Engineering",
    desc: "High-performance web development, headless architectures, React application design, and conversion optimization.",
    url: "https://meraki-solutions.in/services/web-development"
  },
  "/services/ui-ux-design": {
    name: "UI/UX Design & Prototyping",
    desc: "User-centric UI/UX design, interactive wireframing, component-driven layouts, and seamless digital product design.",
    url: "https://meraki-solutions.in/services/ui-ux-design"
  },
  "/services/social-media": {
    name: "Social Media & Content Strategy",
    desc: "Transform your social presence with cinematic content and data-driven engagement strategies.",
    url: "https://meraki-solutions.in/services/social-media"
  },
  "/services/performance-marketing": {
    name: "Performance Marketing & Growth",
    desc: "Scalable performance marketing, ad creative curation, ROI-driven campaigns, and multi-channel acquisition.",
    url: "https://meraki-solutions.in/services/performance-marketing"
  }
};

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website' 
}) => {
  const siteTitle = title ? `${title} | ${siteMeta.name}` : `${siteMeta.name} | ${siteMeta.tagline || 'Cinematic Storytelling & Digital Engineering'}`;
  const siteDescription = description || siteMeta.description;
  const siteKeywords = keywords || "Meraki Solutions, Cinematic Storytelling, Digital Engineering, Boutique Studio, Brand Engineering, Film Production, Technology Studio, Bhopal, Madhya Pradesh";
  
  // Standardized domain
  const baseUrl = 'https://meraki-solutions.in';
  const siteUrl = url || baseUrl; 
  
  // Use original logo as requested, ensure absolute path
  const siteImage = image || `${baseUrl}/meraki_solutions_logo.png`; 

  // Parse path to inject schema structured data (BreadcrumbList, Service, CollectionPage)
  const rawPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const currentPath = rawPath.replace(/\/$/, '').toLowerCase(); // normalized

  let breadcrumbSchema = null;
  let dynamicSchema = null;

  if (currentPath === '/services') {
    // Parent Services Page
    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://meraki-solutions.in"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": "https://meraki-solutions.in/services"
        }
      ]
    };

    dynamicSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Our Services | Meraki Solutions",
      "description": "Explore our suite of 8 core services covering branding, web engineering, film making, and performance marketing.",
      "url": "https://meraki-solutions.in/services",
      "hasPart": Object.keys(serviceMap).map((path) => ({
        "@type": "Service",
        "name": serviceMap[path].name,
        "description": serviceMap[path].desc,
        "url": `https://meraki-solutions.in${path}`,
        "provider": {
          "@type": "Organization",
          "name": "Meraki Solutions",
          "url": "https://meraki-solutions.in"
        }
      }))
    };
  } else if (serviceMap[currentPath]) {
    // Child Service Page
    const serviceInfo = serviceMap[currentPath];
    
    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://meraki-solutions.in"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": "https://meraki-solutions.in/services"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": serviceInfo.name,
          "item": serviceInfo.url
        }
      ]
    };

    dynamicSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": serviceInfo.name,
      "description": serviceInfo.desc,
      "url": serviceInfo.url,
      "provider": {
        "@type": "Organization",
        "name": "Meraki Solutions",
        "url": "https://meraki-solutions.in"
      },
      "serviceType": serviceInfo.name,
      "areaServed": "Worldwide"
    };
  }

  return (
    <Helmet>
      {/* Standard metadata */}
      <title>{siteTitle}</title>
      <meta name="description" content={siteDescription} />
      <meta name="keywords" content={siteKeywords} />
      <link rel="canonical" href={siteUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:image" content={siteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={siteUrl} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={siteImage} />

      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" />
      <meta name="theme-color" content="#14110f" />

      {/* Schema Structured Data Injection */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {dynamicSchema && (
        <script type="application/ld+json">
          {JSON.stringify(dynamicSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
